import { motion } from "framer-motion";
import { Music2, ExternalLink, Headphones, Mic, Globe, Play, Zap } from "lucide-react";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/lib/LanguageContext";
import { springPresets, staggerContainer, staggerItem } from "@/lib/motion";
import { EXTERNAL_LINKS } from "@/lib/index";

export default function RidmixPage() {
  const { t, language } = useLanguage();

  const GENRES = [
    "Cinematic Music", "Ambient & Soundscapes", "Electronic", "Deep Relax",
    "Space Music", "Piano Reflections", "Instrumental", "Science Fiction Score",
  ];

  const LINKS = [
    {
      href: EXTERNAL_LINKS.RIDMIX,
      label: "linktr.ee/ridmix",
      icon: Globe,
      desc: language === "it"
        ? "Hub centrale con tutti i canali e le piattaforme di Ridmix"
        : language === "de"
        ? "Zentraler Hub mit allen Ridmix-Kanälen und Plattformen"
        : "Central hub with all Ridmix channels and platforms",
    },
    {
      href: "https://www.instagram.com/reel/DVwRl0iCNzO/",
      label: language === "it" ? "Ridmix su Instagram" : language === "de" ? "Ridmix auf Instagram" : "Ridmix on Instagram",
      icon: Play,
      desc: language === "it"
        ? "Video e contenuti musicali su Instagram"
        : language === "de"
        ? "Videos und Musikinhalte auf Instagram"
        : "Videos and music content on Instagram",
    },
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-12">
        {/* ── Enhanced Hero Section with Banner ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="relative rounded-[2rem] overflow-hidden border border-border/40 group shadow-2xl"
          style={{ background: "oklch(0.08 0.02 240)" }}
        >
          {/* Hero Banner Image */}
          <div className="absolute inset-0 z-0">
            <img 
              src="/ridmix_banner.jpg" 
              alt="Ridmix Music Banner" 
              className="w-full h-full object-cover opacity-50 group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          </div>

          {/* Content Over Banner */}
          <div className="relative z-10 p-8 sm:p-12 flex flex-col items-center text-center space-y-8">
            {/* Logo Badge */}
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-[2.5rem] bg-black/80 flex items-center justify-center ring-4 ring-accent/40 p-1 shadow-2xl overflow-hidden"
            >
              <img src="/ridmix_logo.png" alt="Ridmix Logo" className="w-full h-full object-cover" />
            </motion.div>

            <div className="space-y-4 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/20 text-accent text-xs font-bold tracking-widest uppercase border border-accent/30">
                <Headphones className="w-3.5 h-3.5" />
                Music That Moves People
              </div>
              <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight">
                {t("ridmix.title")}
              </h1>
              <p className="text-lg sm:text-xl text-accent font-bold">
                {t("ridmix.subtitle")}
              </p>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed max-w-2xl mx-auto font-medium">
                {t("ridmix.description")}
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <a
                href={EXTERNAL_LINKS.RIDMIX}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-accent text-accent-foreground text-sm font-black hover:bg-accent/90 transition-all shadow-xl shadow-accent/25"
              >
                <Music2 className="w-5 h-5" />
                Explore Ridmix
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* About Section */}
        <div className="grid lg:grid-cols-3 gap-8">
          <motion.section
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="lg:col-span-2 rounded-3xl border border-border/60 bg-card p-8 sm:p-10 space-y-6 shadow-xl shadow-black/20"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                <Mic className="w-5 h-5 text-accent" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">{t("ridmix.aboutTitle")}</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                {language === "it" 
                  ? "Ridouan Labyad, in arte RIDMIX, è un produttore musicale, systems thinker e storyteller globale. La sua musica è al crocevia tra tecnologia, emozione e architettura sonora."
                  : language === "de"
                  ? "Ridouan Labyad, besser bekannt als RIDMIX, ist Musikproduzent, Systemdenker und globaler Storyteller. Seine Musik steht an der Schnittstelle von Technologie, Emotion und Klangarchitektur."
                  : "Ridouan Labyad, aka RIDMIX, is a music producer, systems thinker, and global storyteller. His music is at the crossroads of technology, emotion, and sound architecture."}
              </p>
              <p>
                {language === "it"
                  ? "Ogni traccia è progettata per creare un'esperienza immersiva e significativa, usando il suono come linguaggio universale per connettere storie e visioni del futuro."
                  : language === "de"
                  ? "Jeder Track ist so konzipiert, dass er ein immersives und bedeutungsvolles Erlebnis schafft, wobei Klang als universelle Sprache verwendet wird, um Geschichten und Zukunftsvisionen zu verbinden."
                  : "Each track is designed to create an immersive and meaningful experience, using sound as a universal language to connect stories and visions of the future."}
              </p>
              <blockquote className="border-l-4 border-accent pl-6 py-4 italic text-lg text-foreground/80 bg-accent/5 rounded-r-2xl">
                "RIDMIX builds sound the way others build systems."
                <footer className="text-sm mt-2 font-medium text-muted-foreground">— Giovanni Burdi, LinkedIn Pulse</footer>
              </blockquote>
            </div>
          </motion.section>

          {/* Genres & Links */}
          <div className="space-y-8">
            {/* Genres */}
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.15 }}
              className="rounded-3xl border border-border/60 bg-card p-6 space-y-4 shadow-xl shadow-black/20"
            >
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent" />
                {t("ridmix.genres")}
              </h2>
              <div className="flex flex-wrap gap-2">
                {GENRES.map((genre, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-xl bg-accent/10 text-accent text-xs font-semibold border border-accent/20">
                    {genre}
                  </span>
                ))}
              </div>
            </motion.section>

            {/* Links */}
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
              className="rounded-3xl border border-border/60 bg-card p-6 space-y-4 shadow-xl shadow-black/20"
            >
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Globe className="w-5 h-5 text-accent" />
                {t("ridmix.whereToFind")}
              </h2>
              <div className="space-y-3">
                {LINKS.map((link, i) => (
                  <a
                    key={i}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-2xl border border-border/40 bg-card hover:border-accent/50 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20">
                      <link.icon className="w-5 h-5 text-accent" />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="text-sm font-bold text-foreground truncate">{link.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{link.desc}</p>
                    </div>
                  </a>
                ))}
              </div>
            </motion.section>
          </div>
        </div>
      </div>
    </Layout>
  );
}
